import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Building2, User, Phone, Mail, MapPin, Upload, Shield, Loader2, Check } from 'lucide-react';

const CompanyProfile = ({ user, onUpdateProfile }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    company_name: user?.company_name || '',
    contact_person: user?.contact_person || user?.name || '',
    cif_dni: user?.cif_dni || '',
    country: user?.country || 'ES',
    phone: user?.phone || '',
    email: user?.email || '',
    address: user?.address || '',
    company_logo: user?.company_logo || null
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        company_name: user.company_name || '',
        contact_person: user.contact_person || user.name || '',
        cif_dni: user.cif_dni || '',
        country: user.country || 'ES',
        phone: user.phone || '',
        email: user.email || '',
        address: user.address || '',
        company_logo: user.company_logo || null
      });
    }
  }, [user]);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo file size must be less than 2MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, company_logo: reader.result }));
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('session_token');

      const response = await fetch(`${backendUrl}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          company_name: formData.company_name,
          contact_person: formData.contact_person,
          cif_dni: formData.cif_dni,
          country: formData.country,
          phone: formData.phone,
          address: formData.address,
          company_logo: formData.company_logo
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedUser = await response.json();
      if (onUpdateProfile) {
        onUpdateProfile(updatedUser);
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(t('profile_update_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError(t('passwords_dont_match'));
      return;
    }

    if (passwordData.new_password.length < 8) {
      setError(t('password_requirements'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('session_token');

      const response = await fetch(`${backendUrl}/api/profile/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to change password');
      }

      setSuccess(true);
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || t('password_update_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">
      {/* Success Message */}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check className="w-5 h-5" />
          <span>{t('profile_updated')}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Company Logo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" style={{color: '#36e961'}} />
            {t('company_logo')}
          </CardTitle>
          <CardDescription>{t('company_logo_desc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {/* Logo Preview */}
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
              {formData.company_logo ? (
                <img src={formData.company_logo} alt="Company Logo" className="w-full h-full object-contain" />
              ) : (
                <Building2 className="w-12 h-12 text-gray-400" />
              )}
            </div>

            {/* Upload Button */}
            <div className="flex-1">
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <Label htmlFor="logo-upload">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('logo-upload').click()}
                  className="cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {formData.company_logo ? t('change_logo') : t('upload_logo')}
                </Button>
              </Label>
              <p className="text-sm text-gray-600 mt-2">Formato: PNG, JPG • Tamaño máximo: 2MB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" style={{color: '#36e961'}} />
            {t('company_information')}
          </CardTitle>
          <CardDescription>{t('company_info_billing_desc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('company_name_required')}</Label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder={t('ph_company_example')}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t('cif_dni_required')}</Label>
              <Input
                value={formData.cif_dni}
                onChange={(e) => setFormData(prev => ({ ...prev, cif_dni: e.target.value }))}
                placeholder={t('cif_dni_placeholder')}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>País del CIF / VAT *</Label>
              <Select
                value={formData.country}
                onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('ph_select_country')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ES">🇪🇸 ES VAT (España)</SelectItem>
                  <SelectItem value="FR">🇫🇷 FR VAT (Francia)</SelectItem>
                  <SelectItem value="DE">🇩🇪 DE VAT (Alemania)</SelectItem>
                  <SelectItem value="IT">🇮🇹 IT VAT (Italia)</SelectItem>
                  <SelectItem value="PT">🇵🇹 PT VAT (Portugal)</SelectItem>
                  <SelectItem value="UK">🇬🇧 UK VAT (Reino Unido)</SelectItem>
                  <SelectItem value="BE">🇧🇪 BE VAT (Bélgica)</SelectItem>
                  <SelectItem value="NL">🇳🇱 NL VAT (Países Bajos)</SelectItem>
                  <SelectItem value="OTHER">🌍 Sin VAT (Otro país)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('company_address')}</Label>
            <Textarea
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              placeholder={t('address_placeholder')}
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" style={{color: '#36e961'}} />
            {t('contact_information')}
          </CardTitle>
          <CardDescription>Datos de la persona de contacto</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('contact_person_required')}</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                placeholder={t('ph_name_example')}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t('phone_required')}</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder={t('ph_phone_es')}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('email_address')}</Label>
            <Input
              type="email"
              value={formData.email}
              disabled
              className="bg-gray-100"
            />
            <p className="text-xs text-gray-500">{t('email_readonly_note')}</p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSaveProfile}
          disabled={loading}
          className="text-white"
          style={{backgroundColor: '#36e961'}}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Guardando...
            </>
          ) : (
            t('save_changes')
          )}
        </Button>
      </div>

      {/* Change Password (only for admin users) */}
      {user?.role === 'admin' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" style={{color: '#36e961'}} />
              {t('change_password')}
            </CardTitle>
            <CardDescription>{t('change_password_section_desc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>{t('current_password')}</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('new_password')}</Label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('confirm_password')}</Label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">{t('password_requirements')}</p>
            <Button
              onClick={handleChangePassword}
              disabled={loading}
              variant="outline"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Actualizando...
                </>
              ) : (
                t('change_password')
              )}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CompanyProfile;
